// Returns whether background polling should be active for the current request.
// Polling runs ONLY on the prod host AND within office hours (Mon–Fri 07:00–17:00 ET).
// Consumed per-site as: refetchInterval = pollActive ? ms : false.
//
// Dev (any non-prod host) → always false → zero auto-poll egress. The same code
// bundle ships to Dev and Prod, so isProdHost() (hostname check) is the only
// runtime env signal; a Dev tab left open during the workday would otherwise poll
// identically to Prod and burn free-tier Supabase egress.
//
// The internal setInterval is a pure in-memory clock check (NO network) that
// re-evaluates every minute and flips the boolean at the 07:00/17:00 boundaries.
// That re-render is what makes React Query resume/pause: a bare
// `refetchInterval: () => active ? ms : false` would not work, because RQ clears
// its timer when the fn returns false and (with refetchOnWindowFocus disabled
// globally) never re-evaluates it — so the gate would never re-open at 07:00.

import { useState, useEffect } from 'react';
import { isProdHost } from '../lib/env.js';
import { isWithinWorkingHours } from '../lib/working-hours.js';

const CHECK_INTERVAL_MS = 60_000;

export function usePollActive(): boolean {
  const [active, setActive] = useState(isWithinWorkingHours);

  useEffect(() => {
    const id = setInterval(() => setActive(isWithinWorkingHours()), CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // isProdHost() read per-render — hostname can't change in-session, so no effect needed.
  return isProdHost() && active;
}
